/**
 * Notificações do TARP crítica×secundária, profile-aware (GUPM-177). TS puro.
 * Fiel ao `buildNotifications` do protótipo v5.10: o MESMO evento gera texto e
 * relevância diferentes conforme o perfil (operador vs RT).
 *
 * Envio real (Teams/SMS/cadeia de escalonamento) é stub — Blocked-by-external
 * (GUPM-23/24). Aqui só o CONTEÚDO por perfil.
 */
import { rotuloDaEstrutura } from '../estrutura/rotulo';
import type { TarpLevel } from '../types';
import type { TarpRecordStatus } from './status';

export type NotifSeveridade = 'EMERGENCIA' | 'ALERTA' | 'ATENCAO' | 'INFO';
export type NotifRelevancia = 'critica' | 'secundaria';
export type NotifProfile = 'operador' | 'tecnico_responsavel' | 'admin';

export interface Notificacao {
  sev: NotifSeveridade;
  relev: NotifRelevancia;
  titulo: string;
  descricao: string;
}

export interface TarpEventoNotificavel {
  tarpCode: string;
  status: TarpRecordStatus;
  nivel: TarpLevel;
  damCode: string;
  instrumentCode?: string | null;
}

export function severidadeFromNivel(nivel: TarpLevel): NotifSeveridade {
  return nivel === 'NORMAL' ? 'INFO' : nivel;
}

function isRt(profile: NotifProfile): boolean {
  return profile === 'tecnico_responsavel' || profile === 'admin';
}

/**
 * Constrói a notificação de um TARP para um perfil. A relevância e o texto
 * mudam por perfil (crítica = você precisa agir; secundária = ciência).
 */
export function buildTarpNotificacao(
  ev: TarpEventoNotificavel,
  profile: NotifProfile,
): Notificacao {
  const sev = severidadeFromNivel(ev.nivel);
  // C-24 — estrutura sempre por extenso: o sino mostra este texto ao vigilante, e "A · PZ-05"
  // sozinho não diz que "A" é a barragem. Pelo helper (GUPM-329), para a Mina não virar
  // "Barragem MINA". `damCode` vazio = TARP aberta sem barragem (GUPM-186).
  const barragem = rotuloDaEstrutura(ev.damCode) ?? 'Estrutura não identificada';
  const escopo = ev.instrumentCode ? `${barragem} · ${ev.instrumentCode}` : barragem;
  const rt = isRt(profile);
  const isEmergencia = sev === 'EMERGENCIA';

  switch (ev.status) {
    case 'CONCLUIDA_AGUARDANDO':
      return rt
        ? {
            sev,
            relev: 'critica',
            titulo: `TARP ${ev.tarpCode} aguardando baixa`,
            descricao: `${escopo} — concluída pelo operador. Avalie e dê a baixa técnica.`,
          }
        : {
            sev: 'INFO',
            relev: 'secundaria',
            titulo: `TARP ${ev.tarpCode} concluída`,
            descricao: `${escopo} — encaminhada ao RT. Você não precisa mais agir nesta TARP.`,
          };

    case 'EM_ANALISE_RT':
      return rt
        ? {
            sev,
            relev: 'critica',
            titulo: `TARP ${ev.tarpCode} em sua análise`,
            descricao: `${escopo} — aguardando avaliação técnica e baixa.`,
          }
        : {
            sev: 'ATENCAO',
            relev: 'secundaria',
            titulo: `TARP ${ev.tarpCode} em análise pelo RT`,
            descricao: `${escopo} — o responsável técnico está avaliando.`,
          };

    case 'ENCERRADA':
      return {
        sev: 'INFO',
        relev: 'secundaria',
        titulo: `TARP ${ev.tarpCode} baixada`,
        descricao: `${escopo} — baixa registrada pelo RT.`,
      };

    case 'ABERTA':
    default:
      if (isEmergencia) {
        return {
          sev,
          relev: 'critica',
          titulo: `EMERGÊNCIA — ${escopo}`,
          descricao: `Acione o protocolo TARP ${ev.tarpCode} imediatamente.`,
        };
      }
      return rt
        ? {
            sev,
            relev: 'secundaria',
            titulo: `TARP ${ev.tarpCode} aberta`,
            descricao: `${escopo} — acompanhe a execução do operador.`,
          }
        : {
            sev,
            relev: 'critica',
            titulo: `Ação necessária — ${escopo}`,
            descricao: `Siga o protocolo TARP ${ev.tarpCode}.`,
          };
  }
}
